import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TeacherTrialStats } from "@/hooks/useTeacherTrialStats";
import { TeacherTrialDetailModal } from "./TeacherTrialDetailModal";
import { SortableTableHead } from "@/components/admin/salary/SortableTableHead";

type SortField = 'teacher_code' | 'teacher_nickname' | 'total_trials' | 'successful_trials' | 'success_rate';

interface TeacherTrialStatsTableProps {
  data: TeacherTrialStats[];
  isLoading: boolean;
  startDate?: string;
  endDate?: string;
}

export const TeacherTrialStatsTable: React.FC<TeacherTrialStatsTableProps> = ({
  data,
  isLoading,
  startDate,
  endDate,
}) => {
  const [selectedTeacher, setSelectedTeacher] = useState<TeacherTrialStats | null>(null);
  const [sortField, setSortField] = useState<SortField>('total_trials');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field as SortField);
      setSortDirection('desc');
    }
  };

  const sortedData = useMemo(() => {
    if (!data) return [];
    return [...data].sort((a, b) => {
      let aVal: string | number = a[sortField] ?? '';
      let bVal: string | number = b[sortField] ?? '';

      // Handle string comparison for teacher_nickname (use teacher_name as fallback)
      if (sortField === 'teacher_nickname') {
        aVal = (a.teacher_nickname || a.teacher_name || '').toLowerCase();
        bVal = (b.teacher_nickname || b.teacher_name || '').toLowerCase();
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal as string).toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortField, sortDirection]);

  const getProgressColor = (rate: number) => {
    if (rate >= 70) return "bg-green-500";
    if (rate >= 40) return "bg-yellow-500";
    return "bg-red-500";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        ไม่พบข้อมูลทดลองเรียน
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableTableHead
                field="teacher_code"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                className="w-[100px]"
              >
                รหัสครู
              </SortableTableHead>
              <SortableTableHead
                field="teacher_nickname"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              >
                ชื่อครู
              </SortableTableHead>
              <SortableTableHead
                field="total_trials"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                className="text-center w-[120px]"
              >
                ทดลองเรียน
              </SortableTableHead>
              <SortableTableHead
                field="successful_trials"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                className="text-center w-[120px]"
              >
                สำเร็จ
              </SortableTableHead>
              <SortableTableHead
                field="success_rate"
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                className="w-[200px]"
              >
                อัตราสำเร็จ
              </SortableTableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedData.map((teacher) => (
              <TableRow 
                key={teacher.teacher_code}
                className="cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => setSelectedTeacher(teacher)}
              >
                <TableCell className="font-medium">{teacher.teacher_code}</TableCell>
                <TableCell>
                  {teacher.teacher_nickname || teacher.teacher_name || "-"}
                </TableCell>
                <TableCell className="text-center">{teacher.total_trials}</TableCell>
                <TableCell className="text-center">{teacher.successful_trials}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 relative h-2 w-full overflow-hidden rounded-full bg-secondary">
                      <div 
                        className={`h-full transition-all ${getProgressColor(teacher.success_rate)}`}
                        style={{ width: `${teacher.success_rate}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium w-[50px] text-right">
                      {teacher.success_rate.toFixed(1)}%
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <TeacherTrialDetailModal
        isOpen={!!selectedTeacher}
        onClose={() => setSelectedTeacher(null)}
        teacher={selectedTeacher}
        startDate={startDate}
        endDate={endDate}
      />
    </>
  );
};
